/* jshint esversion: 6 */
import React from 'react';
import PropTypes from 'prop-types';
import InputBehaviorComponent from 'app/components/EditorComponents/InputBehaviorComponent.jsx';
import ErrorModule from 'app/mixins/ErrorModule.jsx';
import FieldError from 'app/components/common/FieldError.jsx';
import HelpButton from 'app/components/common/HelpButton.jsx';
import { assetBase } from '@/lib/asset-base.ts';
import { ConfigureStep } from '@/shadcn/components/NewAutomation/ConfigureStep.tsx';
import { etsyShopName, pinterestUsername } from '@/lib/subtype-identifier.ts';


class CreateSubtypePanel extends React.Component {
    /*
     * The wizard subtitle for a guided subtype, per the design's configure
     * frames — each source names the thing it posts. Static because the shell
     * that renders it (AutomationShell, in RouteNew) sits above this component,
     * and the copy belongs with the fields it describes. Returns undefined for a
     * subtype with no frame, which leaves the shell's own default in place.
     */
    /*
     * Keyed by whatever `RouteNew.state.inputType` holds, which is an
     * INPUT_SUBTYPE_* for the guided sources and INPUT_TYPE_TWITTER for the one
     * type that also lands on this step. The two spaces don't collide —
     * subtypes are 10-18, input types are 100+ — so one map is safe here.
     */
    static subtitleFor(subtype, constants) {
        let subtitles = {};

        subtitles[constants.INPUT_SUBTYPE_YOUTUBE]   = "Choose which videos we'll post.";
        subtitles[constants.INPUT_SUBTYPE_PINTEREST] = "Choose which pins we'll post.";
        subtitles[constants.INPUT_SUBTYPE_ETSY]      = "Choose which listings we'll post.";
        subtitles[constants.INPUT_SUBTYPE_SHOPIFY]   = "Choose which products we'll post.";
        subtitles[constants.INPUT_SUBTYPE_SUBSTACK]  = "Choose which posts we'll share.";
        subtitles[constants.INPUT_SUBTYPE_VIMEO]     = "Choose which videos we'll post.";
        subtitles[constants.INPUT_SUBTYPE_EBAY]      = "Choose which listings we'll post.";
        subtitles[constants.INPUT_TYPE_TWITTER]      = "Choose which posts we'll share.";

        return subtitles[parseInt(subtype)];
    }

    constructor(props) {
        super(props);

        this.subtypeUsernameHandler = this.subtypeUsernameHandler.bind(this);
        this.onFieldChange = this.onFieldChange.bind(this);

    }

    /*
     * ConfigureStep reports a change as (fieldSpec, value); the form machinery
     * here wants a DOM-ish event carrying a `data-field` path. This adapts one to
     * the other so the new step feeds the same changeHandler as the legacy
     * fieldsets, with the same statePath and the same validation on Next.
     *
     * Fields that name the input mirror their value into `.name` as well, which
     * is what subtypeUsernameHandler does for the legacy controls. The optional
     * board/collection fields deliberately don't — they're a refinement of an
     * input that is already named.
     */
    onFieldChange(field, value) {
        let event = {
                currentTarget: {
                    dataset: {
                        field: this.props.statePath + '.' + field.name
                    }
                },
                target: {value: this.normalizeIdentifier(field.name, value)}
            },
            namesInput = field.name !== 'subtypeBoard';

        if (namesInput) {
            this.subtypeUsernameHandler(event);
        } else {
            this.props.changeHandler(event);
        }
    }

    /* Validation feedback for one backing field, as ConfigureStep renders it. */
    fieldError(keys, fields) {
        return (
            <ErrorModule
                app={this.props.app}
                keys={keys}
                render={(state) => {
                    return (
                        <div>
                            {fields.map((field) => {
                                return (
                                    <FieldError
                                        key={field}
                                        field={field}
                                        errors={state.errors}/>
                                );
                            })}
                        </div>
                    );
                }}/>
        );
    }

    /*
     * The new-design configure step for a guided subtype: the design's labels,
     * placeholders and helper copy, against prod's backing fields.
     *
     * The Pinterest and Etsy fields invite a pasted profile/shop URL, and the
     * backend interpolates whatever is stored into a URL of its own — so the
     * paste is normalised on the way in rather than stored verbatim. See
     * normalizeIdentifier and @/lib/subtype-identifier. What follows described
     * that as an accepted hazard; it was not one, because the copy asks for the
     * value that breaks:
     *
     * The legacy fieldsets avoided it by printing the
     * prefix beside a username-only box. Resolve it by parsing the paste in
     * subtypeUsernameHandler, not by re-adding the prefix here.
     */
    configureSpec() {
        let constants = this.props.app.config.constants,
            input = this.props.input,
            errorKeys = ['CURATION_INPUT_CREATE', 'INPUT_UPDATE', 'INPUT_CREATE', 'INPUT_VALIDATE'];

        switch (parseInt(input.subtype)) {
            case constants.INPUT_SUBTYPE_PINTEREST:
                return {
                    account: {name: 'Pinterest', monogram: 'P', network: 'pinterest'},
                    fields: [
                        {
                            label: 'Pinterest username',
                            name: 'subtypeUsername',
                            placeholder: 'pinterest.com/yourname',
                            required: true,
                            value: input.subtypeUsername,
                            help: 'Paste your profile URL or just your username.',
                            error: this.fieldError(errorKeys, ['name', 'subtypeUsername', 'url'])
                        },
                        {
                            label: 'Board (optional)',
                            name: 'subtypeBoard',
                            placeholder: 'board-name',
                            value: input.subtypeBoard,
                            help: "We can't access secret boards, so they won't work here.",
                            error: this.fieldError(errorKeys, ['subtypeBoard'])
                        }
                    ]
                };

            case constants.INPUT_SUBTYPE_YOUTUBE:
                return {
                    account: {name: 'YouTube', monogram: 'Y', network: 'youtube'},
                    fields: [
                        {
                            label: 'YouTube channel',
                            name: 'url',
                            placeholder: 'https://www.youtube.com/@channel',
                            required: true,
                            value: input.url,
                            error: this.fieldError(errorKeys, ['name', 'url'])
                        }
                    ]
                };

            case constants.INPUT_SUBTYPE_ETSY:
                return {
                    account: {name: 'Etsy', monogram: 'E', network: 'etsy'},
                    fields: [
                        {
                            label: 'Etsy shop',
                            name: 'subtypeCompanyName',
                            placeholder: 'etsy.com/shop/YourShop',
                            value: input.subtypeCompanyName,
                            help: 'Paste your shop URL or just the shop name.',
                            error: this.fieldError(errorKeys, ['name', 'subtypeCompanyName', 'url'])
                        }
                    ]
                };

            case constants.INPUT_SUBTYPE_SHOPIFY:
                return {
                    account: {name: 'Shopify', monogram: 'S', network: 'shopify'},
                    fields: [
                        {
                            label: 'Store address',
                            name: 'subtypeUsername',
                            placeholder: 'yourstore.com',
                            required: true,
                            value: input.subtypeUsername,
                            error: this.fieldError(errorKeys, ['name', 'subtypeUsername', 'url'])
                        },
                        {
                            label: 'Collection (optional)',
                            name: 'subtypeBoard',
                            placeholder: 'collection-name',
                            value: input.subtypeBoard,
                            help: 'Leave blank to pull from the whole store.',
                            error: this.fieldError(errorKeys, ['subtypeBoard'])
                        }
                    ]
                };

            case constants.INPUT_SUBTYPE_SUBSTACK:
                return {
                    account: {name: 'Substack', monogram: 'Su', network: 'substack'},
                    fields: [
                        {
                            label: 'Substack address',
                            name: 'subtypeUsername',
                            placeholder: 'yourname.substack.com',
                            required: true,
                            value: input.subtypeUsername,
                            error: this.fieldError(errorKeys, ['name', 'subtypeUsername', 'url'])
                        }
                    ]
                };

            default:
                return null;
        }
    }

    /*
     * A pasted URL reduced to the name the backend expects.
     *
     * Keyed on the FIELD as well as the subtype: Etsy's shop name and Pinterest's
     * username come out of different parts of their URLs, and the board / search
     * fields must not be touched at all.
     */
    normalizeIdentifier(fieldName, value) {
        let constants = this.props.app.config.constants,
            subtype = (this.props.input) ? parseInt(this.props.input.subtype) : 0;

        if (fieldName === 'subtypeUsername' && subtype === constants.INPUT_SUBTYPE_PINTEREST) {
            return pinterestUsername(value);
        }

        if (fieldName === 'subtypeCompanyName' && subtype === constants.INPUT_SUBTYPE_ETSY) {
            return etsyShopName(value);
        }

        return value;
    }

    subtypeUsernameHandler(e) {
        let nameEvent = {
                currentTarget: {
                    dataset: {
                        field: this.props.statePath + '.name'
                    }
                },
                target: {
                    value: e.target.value
                }
            };

        this.props.changeHandler(e);
        this.props.changeHandler(nameEvent);
    }

    render() {
        /*
         * There is one render where this panel is mounted with no input at all.
         * selectInputType blanks editedInput only for the NON-subtype input
         * types, so for a subtype it stays whatever it was — null on a fresh
         * page load — and setState commits that before its callback runs
         * onSubtypeClick to build the record. renderLegacy has always guarded
         * this by returning null; configureSpec reads input.subtype, so the tile
         * path has to guard it too or it dereferences null and takes the step
         * down to the error boundary.
         */
        if (!this.props.input) {
            return null;
        }

        if (this.props.tile) {
            /*
             * No subtype yet is the same transient state as no input: the record
             * exists but createInput hasn't stamped it. Render nothing rather
             * than falling through, which would flash an empty legacy panel.
             */
            if (!this.props.input.subtype) {
                return null;
            }

            let spec = this.configureSpec();

            /*
             * eBay and Vimeo sit in RouteNew's guidedInputs but have no case
             * here and no source tile either, so they are unreachable. Falling
             * through to the legacy panel keeps them behaving exactly as they do
             * today rather than rendering an empty new-style step.
             */
            if (spec === null) {
                return this.renderLegacy();
            }

            return (
                <ConfigureStep
                    spec={spec}
                    base={assetBase(this.props.app.config)}
                    onFieldChange={this.onFieldChange} />
            );
        }

        return this.renderLegacy();
    }

    renderLegacy() {
        let behavior = null,
            constants = this.props.app.config.constants,
            fields = null,
            subtypeUsername = 'username',
            subtypeUsernameError = null,
            urlError = null,
            subtypeBoardError = null,
            subtypeCompanyNameError = null,
            subtypeSearchError = null,
            nameError = null,
            errorKeys = ['CURATION_INPUT_CREATE', 'INPUT_UPDATE', 'INPUT_CREATE', 'INPUT_VALIDATE'],
            imgPath = this.props.app.config.assetsUrl + "/" + this.props.app.config.imageUrl;;

        if (this.props.input === null) {
            return null;
        }

        if (this.props.input.subtypeUsername !== undefined) {
            subtypeUsername = this.props.input.subtypeUsername;
        }

        subtypeUsernameError = (
            <ErrorModule
                app={this.props.app}
                keys={errorKeys}
                render={(state) => {
                    return (
                        <FieldError field="subtypeUsername" errors={state.errors}/>
                    );
                }}/>
        );

        nameError = (
            <ErrorModule
                app={this.props.app}
                keys={errorKeys}
                render={(state) => {
                    return (
                        <FieldError field="name" errors={state.errors}/>
                    );
                }}/>
        );

        urlError = (
            <ErrorModule
                app={this.props.app}
                keys={errorKeys}
                render={(state) => {
                    return (
                        <FieldError field="url" errors={state.errors}/>
                    );
                }}/>
        );

        subtypeCompanyNameError = (
            <ErrorModule
                app={this.props.app}
                keys={errorKeys}
                render={(state) => {
                    return (
                        <FieldError field="subtypeCompanyName" errors={state.errors}/>
                    );
                }}/>
        );

        subtypeSearchError = (
            <ErrorModule
                app={this.props.app}
                keys={errorKeys}
                render={(state) => {
                    return (
                        <FieldError field="subtypeCompanyName" errors={state.errors}/>
                    );
                }}/>
        );

        switch (parseInt(this.props.input.subtype)) {
            case constants.INPUT_SUBTYPE_PINTEREST:
                fields = (
                    <div>
                        <div className="input-type-display subtype">
                            <i className="icon ion-social-pinterest"/> Connect Pinterest Feed
                        </div>
                        <fieldset>
                            <legend>Pinterest User <HelpButton which="pinterestFeed"/><span className="required">*</span></legend>
                            {nameError}
                            {subtypeUsernameError}
                            {urlError}
                            {"https://www.pinterest.com/"}<input type="text"
                                className="text-input small"
                                placeholder="username"
                                defaultValue={this.props.input.subtypeUsername}
                                onChange={this.subtypeUsernameHandler}
                                data-field={this.props.statePath + ".subtypeUsername"} />
                        </fieldset>
                        <fieldset>
                            <legend>Pinterest Board (optional)</legend>
                            <ErrorModule
                                app={this.props.app}
                                keys={['CURATION_INPUT_CREATE', 'INPUT_UPDATE', 'INPUT_CREATE', 'INPUT_VALIDATE']}
                                render={(state) => {
                                    return (
                                        <FieldError field="subtypeBoard" errors={state.errors}/>
                                    );
                                }}/>
                            {"https://www.pinterest.com/" + subtypeUsername + "/"}<input type="text"
                                className="text-input small"
                                placeholder="boardname"
                                defaultValue={this.props.input.subtypeBoard}
                                onChange={this.props.changeHandler}
                                data-field={this.props.statePath + ".subtypeBoard"} />
                        </fieldset>
                    </div>
                );
                break;

            case constants.INPUT_SUBTYPE_YOUTUBE:
                fields = (
                    <div>
                        <div className="input-type-display subtype">
                            <i className="icon ion-social-youtube"/> Connect YouTube Input
                        </div>
                        <fieldset>
                            <legend>YouTube Channel<span className="required">*</span></legend>
                            {nameError}
                            {urlError}
                            <input type="text"
                                className="text-input medium flush"
                                placeholder="https://www.youtube.com/@channel"
                                defaultValue={this.props.input.url}
                                onChange={this.subtypeUsernameHandler}
                                data-field={this.props.statePath + ".url"} />
                        </fieldset>
                    </div>
                );
                break;

            case constants.INPUT_SUBTYPE_ETSY:
                // <fieldset>
                //     <legend>Etsy Username</legend>
                //     {nameError}
                //     {subtypeUsernameError}
                //     {urlError}
                //     {"https://www.etsy.com/people/"}<input type="text"
                //         className="text-input med"
                //         placeholder="Username"
                //         defaultValue={this.props.input.subtypeUsername}
                //         onChange={this.subtypeUsernameHandler}
                //         data-field={this.props.statePath + ".subtypeUsername"} />{"/favorites/items.rss"}
                // </fieldset>
                fields = (
                    <div>
                        <div className="input-type-display subtype">
                            <img src={`${imgPath}/svg-logos/etsy.svg`} width="40" height="auto"/> Connect Etsy Shop
                        </div>
                        <fieldset>
                            <legend>Etsy Shop Name</legend>
                            {nameError}
                            {subtypeCompanyNameError}
                            {urlError}
                            {"https://www.etsy.com/shop/"}<input type="text"
                                className="text-input medium"
                                placeholder="shopname"
                                defaultValue={this.props.input.subtypeCompanyName}
                                onChange={this.subtypeUsernameHandler}
                                data-field={this.props.statePath + ".subtypeCompanyName"} />
                        </fieldset>
                    </div>
                );
                break;

            case constants.INPUT_SUBTYPE_SHOPIFY:
                fields = (
                    <div>
                        <div className="input-type-display subtype">
                            <i className="ds-icon icon-shopify"/> Connect Shopify
                        </div>
                        <fieldset>
                            <legend>Shopify Store <span className="required">*</span></legend>
                            {nameError}
                            {subtypeUsernameError}
                            {urlError}
                            {"https://"}<input type="text"
                                className="text-input small"
                                placeholder="yourstore.com"
                                defaultValue={this.props.input.subtypeUsername}
                                onChange={this.subtypeUsernameHandler}
                                data-field={this.props.statePath + ".subtypeUsername"} />
                        </fieldset>
                        <fieldset>
                            <legend>Shopify Collection (optional)</legend>
                            <ErrorModule
                                app={this.props.app}
                                keys={['CURATION_INPUT_CREATE', 'INPUT_UPDATE', 'INPUT_CREATE', 'INPUT_VALIDATE']}
                                render={(state) => {
                                    return (
                                        <FieldError field="subtypeBoard" errors={state.errors}/>
                                    );
                                }}/>
                            {"https://" + subtypeUsername + "/collections/"}<input type="text"
                                className="text-input small"
                                placeholder="collectionname"
                                defaultValue={this.props.input.subtypeBoard}
                                onChange={this.props.changeHandler}
                                data-field={this.props.statePath + ".subtypeBoard"} />
                        </fieldset>
                    </div>
                );
                break;

            case constants.INPUT_SUBTYPE_SUBSTACK:
                fields = (
                    <div>
                        <div className="input-type-display subtype">
                            <i className="ds-icon icon-substack"/> Connect Substack
                        </div>
                        <fieldset>
                            <legend>Substack URL <span className="required">*</span></legend>
                            {nameError}
                            {subtypeUsernameError}
                            {urlError}
                            {"https://"}<input type="text"
                                className="text-input small"
                                placeholder="your Substack URL"
                                defaultValue={this.props.input.subtypeUsername}
                                onChange={this.subtypeUsernameHandler}
                                               data-field={this.props.statePath + ".subtypeUsername"} />
                        </fieldset>
                    </div>
                );
                break;

        }

        return (
            <div className="editor-panel subtype">
                {fields}
            </div>
        );
    }
}

CreateSubtypePanel.displayName = "CreateSubtypePanel";

CreateSubtypePanel.propTypes = {
    /** Renders the new-design configure step instead of the legacy fieldsets. */
    tile: PropTypes.bool
};

CreateSubtypePanel.defaultProps = {
    tile: false
};

export default CreateSubtypePanel;
